/**
 * MODULE: _shared/frontend-shell/src/config/routes.config.ts
 * PURPOSE: Centralized route definitions and lazy-loaded page components for the shell
 * DEPENDENCIES: react (lazy, ComponentType, LazyExoticComponent)
 * PUBLIC API: ROUTES, RouteConfig, RouteMetadata, getNavItems, getRouteByPath
 * SIDE EFFECTS: None (pure configuration)
 * AGENT SAFETY: 🟡 RESTRICTED — routing structure; lazy components must satisfy RouteConfig typing
 * FILE SIZE: ~1037 / 300 target (split recommended per slice guide)
 *
 * Agent notes:
 * - Add new routes here, not in App.tsx; paths absolute; nav from nav.ts not showInNav
 * - Exception: `/tenant-admin/*` nested layout (TenantAdminLayout + Outlet) lives in App.tsx to avoid double-wrapping.
 * - Lab routes: "LAB ROUTE" comment, showInNav: false
 *
 * CHANGE LOG:
 * - 2026-10-09 [2.2.2164]: **`/home`**, **`/assistant`**, and **`/langflow-assistant`** load **`AgentHomeRedirect`**.
 * - 2026-10-09 [2.2.2163]: **`DASHBOARD`** (`/` and `/dashboard`) loads **`AgentHomeRedirect`** — signed-in home is `/agent`.
 * - 2026-10-02 [2.2.2156]: **`AUTH_WELCOME`** `/auth/welcome` is a public signup screen. It is no longer caught by the logged-out 404 redirect to `/auth`.
 * - 2026-07-16 [2.2.1604]: **`SETTINGS_INTEGRATIONS`** `/settings/integrations` (+ splat) → **`/settings?tab=integrations`**.
 * - 2026-06-13 [2.2.1383]: **`SUBSCRIPTION_SETUP`** `requiresAuth: false` for pay-first signup; **`showLayout: false`** (no sidebar escape).
 * - 2026-05-20 Sprint 10 [2.2.1063]: **`REVOPS_ACTIVITY_FEED`** `/activity` — lazy **`RevOpsActivityFeedPage`** (world-model timeline; mock until B-0).
 * - 2026-05-08 [2.2.884]: **`ROUTES.CRM`** metadata includes **tasks** in the hub description (**`/crm/tasks`** route lives in **`CrmModule.tsx`**); root **`CHANGELOG` [2.2.884]`**
 * - 2026-04-19: **`CALENDAR_EVENT`** (`/calendar/events/:eventId`) — full-screen event editor (CAL G22).
 * - 2026-04-18: **`SETTINGS_PROFILE`** (`/settings/profile`) and **`SETTINGS_WORKSPACE`** (`/settings/workspace`) redirect to **`/settings?tab=`** (User menu deep links; Bug 24).
 * - 2026-04-18 [2.2.527]: Removed **`ROUTES.TASKS`** and lazy **`TasksPage`** — **`/tasks`** falls through to **`NotFoundPage`** (Tasks module retired from shell).
 * - 2026-04-18 [2.2.524]: Restored lazy **`TasksPage`** and **`ROUTES.TASKS`** at **`/tasks`** (no sidebar link); fixes stray **`TasksPage`** references and deep links — root **`CHANGELOG` [2.2.524]**.
 * - 2026-04-18 [2.2.523]: Removed Tasks sidebar nav item; **`TASKS`** route briefly removed then restored in [2.2.524].
 * - 2026-04-13 [2.2.414]: EMAIL `/email` → lazy `EmailToMessagingRedirect` (canonical inbox is `/messaging`); Playwright **`11-app-shell-navigation`** — **`CHANGELOG` [2.2.414]**.
 * - 2026-05-09 [2.2.894]: **`SETTINGS_BILLING`** `/settings/billing` — lazy **`SettingsWorkspaceBillingPage`**; **`SETTINGS_AI_USAGE`** `/settings/ai-usage` — lazy **`AIUsagePage`** (platform **`GET /api/v1/platform/workspace/quota`**); **`settingsNavSections`** sidebar; root **`CHANGELOG` [2.2.894]`**.
 * - 2026-04-09 [2.2.326]: SETTINGS_EMAIL_ACCOUNTS `/settings/email-accounts` (lazy `InboxSettingsPanel` from `@domain/messaging`).
 * - 2026-04-09 [2.2.325]: MESSAGING_OAUTH_CALLBACK `/messaging/oauth/callback` (lazy `OAuthCallbackPage` from `@domain/messaging`).
 * - 2026-03-30 [2.2.221]: WorkspaceDetailPage lazy → assert LazyExoticComponent<ComponentType<unknown>>;
 *   RevOpsDashboard default import cast ComponentType<unknown> (strict RouteConfig match). See shell README
 *   (Vite cache + lazy typing).
 * - 2026-04-29: Prompt **1-D** — **`CRM`** (`/crm`) layout **`CrmModule`**; nested routes (**`/crm`**, territory, contacts, deals, pipeline-generation, plus **`contacts/:id`** and **`deals/:id`**) in **`App.tsx`**; nav label in **`nav.ts`**.
 * - 2026-04-17: SALES_HUB → SalesEnablementHub at `/sales-hub` (AI Templates page removed; Create tab in shell).
 *   2026-02 auth flow routes; 2026-02-03 SALES_PROFILE paths; 2026-01-27 ASSISTANT /home; COMPANY_DETAIL routes
 *
 * See: _shared/frontend-shell/README.md | /CHANGELOG.md [2.2.221]
 */

import { ComponentType, lazy, LazyExoticComponent } from 'react';

export interface RouteMetadata {
  title: string;
  description?: string;
  requiresAuth: boolean;
  requiresRoles?: string[];
  icon?: string;
  showInNav?: boolean;
  navOrder?: number;
}

export interface RouteConfig {
  path: string;
  component: LazyExoticComponent<ComponentType<unknown>>;
  metadata: RouteMetadata;
}

// Lazy load all pages
const AgentHomeRedirect = lazy(() => import('../pages/AgentHomeRedirect'));
const LoginPage = lazy(() => import('../pages/LoginPage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));
const SettingsPage = lazy(() => import('../pages/SettingsPage'));
const SettingsProfileRedirect = lazy(() =>
  import('../pages/settings/SettingsPathRedirects').then((m) => ({ default: m.SettingsProfileRedirect }))
);
const SettingsWorkspaceRedirect = lazy(() =>
  import('../pages/settings/SettingsPathRedirects').then((m) => ({ default: m.SettingsWorkspaceRedirect }))
);
const SettingsIntegrationsRedirect = lazy(() =>
  import('../pages/settings/SettingsPathRedirects').then((m) => ({
    default: m.SettingsIntegrationsRedirect,
  }))
);
const SuperAdminDashboardPage = lazy(() => import('../pages/SuperAdminDashboardPage'));
const SystemSettingsPage = lazy(() => import('../pages/SystemSettingsPage'));
const TenantManagementPage = lazy(() => import('../pages/TenantManagementPage'));
const FeatureFlagsPage = lazy(() => import('../pages/FeatureFlagsPage'));
const TestResearchPage = lazy(() => import('../pages/TestResearchPage'));
const CrmBrainFixturePage = lazy(() => import('../pages/CrmBrainFixturePage'));
const UnauthorizedPage = lazy(() => import('../pages/UnauthorizedPage'));
const UserManagementPage = lazy(() => import('../pages/UserManagementPage'));
const WorkflowSettingsPage = lazy(() => import('../pages/WorkflowSettingsPage'));
const WorkflowsPage = lazy(() => import('../pages/WorkflowsPage'));
const WorkflowEditorPage = lazy(() => import('../pages/WorkflowEditorPage'));
const ResearchPage = lazy(() => import('../pages/ResearchPage'));
const WorkspaceDetailPage = lazy(() => import('../pages/WorkspaceDetailPage')) as LazyExoticComponent<
  ComponentType<unknown>
>;
const WorkspacesPage = lazy(() => import('../pages/WorkspacesPage'));
const PagesRoot = lazy(() =>
  import('@domain/pages/frontend').then((m) => ({ default: m.PagesRoot as ComponentType<unknown> }))
) as LazyExoticComponent<ComponentType<unknown>>;
const PagesEntryRedirect = lazy(() =>
  import('@domain/pages/frontend').then((m) => ({
    default: m.PagesEntryRedirect as ComponentType<unknown>,
  }))
) as LazyExoticComponent<ComponentType<unknown>>;
const PagesLegacyNoteRedirect = lazy(() =>
  import('@domain/pages/frontend').then((m) => ({
    default: m.PagesLegacyNoteRedirect as ComponentType<unknown>,
  }))
) as LazyExoticComponent<ComponentType<unknown>>;
const BillingPage = lazy(() => import('../pages/BillingPage'));
const BillingUpgradePage = lazy(() => import('../pages/BillingUpgradePage'));
const CalendarPage = lazy(() => import('../pages/CalendarPage'));
const EventEditorPage = lazy(() => import('../pages/calendar/EventEditorPage'));
const BookingPage = lazy(() => import('../pages/BookingPage'));
const CompanyDetailEntry = lazy(() => import('../pages/CompanyDetailEntry'));
const AccountsPage = lazy(() => import('../pages/AccountsPage')) as LazyExoticComponent<
  ComponentType<unknown>
>;
const LegacyOpportunitiesRedirect = lazy(() => import('../pages/LegacyOpportunitiesRedirect'));
const SalesProfilePage = lazy(() => import('../pages/SalesProfilePage'));
const FinancialPage = lazy(() => import('../pages/FinancialPage'));
const ActivityCenterPage = lazy(() => import('../pages/ActivityCenterPage'));
const RevOpsActivityFeedPage = lazy(() => import('../pages/activity/RevOpsActivityFeedPage'));
const RevOpsDashboardPage = lazy(() =>
  import('@domain/revops/frontend/pages/RevOpsDashboard').then((m) => ({
    default: m.RevOpsDashboard as ComponentType<unknown>,
  }))
);
const TerritoryPage = lazy(() => import('../pages/TerritoryPage').then(m => ({ default: m.TerritoryPage })));
const DealsPage = lazy(() => import('../pages/DealsPage').then(m => ({ default: m.DealsPage })));
const SalesCockpitPage = lazy(() => import('../pages/SalesCockpitPage').then(m => ({ default: m.SalesCockpitPage })));
const PipelineGenerationPage = lazy(() =>
  import('../pages/PipelineGenerationPage').then((m) => ({
    default: m.PipelineGenerationPage as ComponentType<unknown>,
  }))
) as LazyExoticComponent<ComponentType<unknown>>;
const SalesEnablementHub = lazy(() =>
  import('../features/sales-enablement-hub').then((m) => ({ default: m.default }))
);
const CrmModule = lazy(() => import('../features/crm').then((m) => ({ default: m.default })));
const ForecastPage = lazy(() => import('@domain/sales-opportunities').then(m => ({ default: m.ForecastPage })));
const ProductsPage = lazy(() => import('../pages/ProductsPage'));
const SalesGuidePage = lazy(() => import('../pages/SalesGuidePage'));
const ForgotPasswordPage = lazy(() => import('@core/auth').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@core/auth').then((m) => ({ default: m.ResetPasswordPage })));
const PricingPage = lazy(() => import('@core/auth').then((m) => ({ default: m.PricingPage })));
const PaymentConfirmationPage = lazy(() => import('@core/auth').then((m) => ({ default: m.PaymentConfirmationPage })));
const AccountSetupPage = lazy(() => import('@core/auth').then((m) => ({ default: m.AccountSetupPage })));
const TenantSelectionPage = lazy(() => import('@core/auth').then((m) => ({ default: m.TenantSelectionPage })));
const WelcomePage = lazy(() => import('@core/auth').then((m) => ({ default: m.WelcomePage })));
const AuthenticationPage = lazy(() => import('../pages/AuthenticationPage'));
const SignupWelcomePage = lazy(() => import('../pages/SignupWelcomePage'));
const SubscriptionPage = lazy(() => import('../pages/SubscriptionPage'));
const MFASetupPageShell = lazy(() => import('../pages/MFASetupPageShell'));
const SessionManagementPage = lazy(() => import('../pages/SessionManagementPage'));
const SecuritySettingsPage = lazy(() => import('../pages/SecuritySettingsPage'));
const AIUsagePage = lazy(() => import('../pages/settings/AIUsagePage'));
const SettingsWorkspaceBillingPage = lazy(() => import('../pages/settings/BillingPage'));
const PresentationsPage = lazy(() => import('../pages/PresentationsPage'));
const TemplateLibraryPage = lazy(() => import('../pages/TemplateLibraryPage'));
const TemplateMarketplacePage = lazy(() =>
  import('@domain/sales-presentations').then((m) => ({ default: m.TemplateMarketplacePage }))
);
const PresentationEditorPage = lazy(() => import('../pages/PresentationEditorPage'));
const ContactsPage = lazy(() => import('../pages/ContactsPage'));
const ReportsPage = lazy(() => import('../pages/ReportsPage'));
const PipelineOptimizePage = lazy(() => import('../pages/PipelineOptimizePage'));
const PipelineCoveragePage = lazy(() => import('../pages/PipelineCoveragePage'));
const CallsPage = lazy(() =>
  import('@domain/call-recording').then((m) => ({ default: m.CallsPage }))
);
const SharedClipPlayerPage = lazy(() =>
  import('@domain/call-recording').then((m) => ({ default: m.SharedClipPlayer as ComponentType<unknown> }))
);
const EmailToMessagingRedirect = lazy(() => import('../pages/EmailToMessagingRedirect'));
const AdminLoginPage = lazy(() =>
  import('@domain/admin/frontend').then((m) => ({ default: m.AdminLoginPage }))
);
const AdminDashboardPage = lazy(() =>
  import('@domain/admin/frontend').then((m) => ({ default: m.AdminDashboardPage }))
);
const AdminTenantListPage = lazy(() =>
  import('@domain/admin/frontend').then((m) => ({ default: m.AdminTenantListPage }))
);
const AdminVouchersPage = lazy(() =>
  import('@domain/admin/frontend').then((m) => ({ default: m.AdminVouchersPage }))
);
const AdminTenantDetailPage = lazy(() =>
  import('@domain/admin/frontend').then((m) => ({ default: m.AdminTenantDetailPage }))
);
const AdminAgentsListPage = lazy(() =>
  import('@domain/admin/frontend').then((m) => ({ default: m.AdminAgentsListPage }))
);
const AdminAgentFormPage = lazy(() =>
  import('@domain/admin/frontend').then((m) => ({ default: m.AdminAgentFormPage }))
);

/**
 * Central route registry
 * 
 * Add new routes here instead of directly in App.tsx
 */
export const ROUTES: Record<string, RouteConfig> = {
  // Public routes
  AUTH: {
    path: '/auth',
    component: AuthenticationPage,
    metadata: {
      title: 'Sign In',
      requiresAuth: false,
      showInNav: false,
    },
  },
  REGISTER: {
    path: '/register',
    component: AuthenticationPage,
    metadata: {
      title: 'Sign Up',
      requiresAuth: false,
      showInNav: false,
    },
  },
  AUTH_WELCOME: {
    path: '/auth/welcome',
    component: SignupWelcomePage,
    metadata: {
      title: 'Welcome to Brazn AI',
      requiresAuth: false,
      showInNav: false,
    },
  },
  LOGIN: {
    path: '/login',
    component: LoginPage,
    metadata: {
      title: 'Login',
      requiresAuth: false,
      showInNav: false,
    },
  },

  UNAUTHORIZED: {
    path: '/unauthorized',
    component: UnauthorizedPage,
    metadata: {
      title: 'Unauthorized',
      requiresAuth: false,
      showInNav: false,
    },
  },

  FORGOT_PASSWORD: {
    path: '/forgot-password',
    component: ForgotPasswordPage,
    metadata: {
      title: 'Forgot Password',
      requiresAuth: false,
      showInNav: false,
    },
  },

  RESET_PASSWORD: {
    path: '/reset-password/:token',
    component: ResetPasswordPage,
    metadata: {
      title: 'Reset Password',
      requiresAuth: false,
      showInNav: false,
    },
  },

  PRICING: {
    path: '/pricing',
    component: PricingPage,
    metadata: {
      title: 'Pricing',
      requiresAuth: false,
      showInNav: false,
    },
  },

  BOOKING_PUBLIC: {
    path: '/book/:slug',
    component: BookingPage,
    metadata: {
      title: 'Book a meeting',
      requiresAuth: false,
      showInNav: false,
    },
  },

  PAYMENT_CONFIRM: {
    path: '/payment-confirm',
    component: PaymentConfirmationPage,
    metadata: {
      title: 'Payment Confirmation',
      requiresAuth: false,
      showInNav: false,
    },
  },

  ACCOUNT_SETUP: {
    path: '/account-setup',
    component: AccountSetupPage,
    metadata: {
      title: 'Account Setup',
      requiresAuth: false,
      showInNav: false,
    },
  },

  ADMIN_LOGIN: {
    path: '/admin/login',
    component: AdminLoginPage,
    metadata: {
      title: 'Admin Login',
      requiresAuth: false,
      showInNav: false,
    },
  },
  ADMIN_DASHBOARD: {
    path: '/admin/dashboard',
    component: AdminDashboardPage,
    metadata: {
      title: 'Admin Dashboard',
      requiresAuth: false,
      showInNav: false,
    },
  },
  ADMIN_TENANTS: {
    path: '/admin/tenants',
    component: AdminTenantListPage,
    metadata: {
      title: 'Admin Tenants',
      requiresAuth: false,
      showInNav: false,
    },
  },
  ADMIN_VOUCHERS: {
    path: '/admin/vouchers',
    component: AdminVouchersPage,
    metadata: {
      title: 'Admin Vouchers',
      requiresAuth: false,
      showInNav: false,
    },
  },
  ADMIN_TENANT_DETAIL: {
    path: '/admin/tenants/:tenantId',
    component: AdminTenantDetailPage,
    metadata: {
      title: 'Admin Tenant Detail',
      requiresAuth: false,
      showInNav: false,
    },
  },
  ADMIN_AGENTS: {
    path: '/admin/agents',
    component: AdminAgentsListPage,
    metadata: {
      title: 'Admin Agents',
      requiresAuth: false,
      showInNav: false,
    },
  },
  ADMIN_AGENTS_NEW: {
    path: '/admin/agents/new',
    component: AdminAgentFormPage,
    metadata: {
      title: 'New Agent',
      requiresAuth: false,
      showInNav: false,
    },
  },
  ADMIN_AGENTS_EDIT: {
    path: '/admin/agents/:agentId/edit',
    component: AdminAgentFormPage,
    metadata: {
      title: 'Edit Agent',
      requiresAuth: false,
      showInNav: false,
    },
  },

  TENANT_SELECT: {
    path: '/tenant-select',
    component: TenantSelectionPage,
    metadata: {
      title: 'Select Organization',
      requiresAuth: false,
      showInNav: false,
    },
  },

  MFA_SETUP: {
    path: '/mfa-setup',
    component: MFASetupPageShell,
    metadata: {
      title: 'Setup Two-Factor Authentication',
      requiresAuth: true,
      showInNav: false,
    },
  },

  WELCOME: {
    path: '/welcome',
    component: WelcomePage,
    metadata: {
      title: 'Welcome',
      requiresAuth: false,
      showInNav: false,
    },
  },

  SUBSCRIPTION_SETUP: {
    path: '/subscription-setup',
    component: SubscriptionPage,
    metadata: {
      title: 'Choose Your Plan',
      requiresAuth: false,
      showInNav: false,
    },
  },

  // Protected routes
  DASHBOARD: {
    path: '/',
    component: AgentHomeRedirect,
    metadata: {
      title: 'Brazn',
      requiresAuth: true,
      showInNav: false,
    },
  },

  WORKSPACES: {
    path: '/workspaces',
    component: WorkspacesPage,
    metadata: {
      title: 'Workspaces',
      requiresAuth: true,
      showInNav: false, // Navigation comes from nav.ts
    },
  },

  WORKSPACE_DETAIL: {
    path: '/workspaces/:workspaceId',
    component: WorkspaceDetailPage,
    metadata: {
      title: 'Workspace Details',
      requiresAuth: true,
      showInNav: false,
    },
  },

  WORKSPACE_SETTINGS: {
    path: '/workspaces/:workspaceId/settings',
    component: WorkspaceDetailPage,
    metadata: {
      title: 'Workspace Settings',
      requiresAuth: true,
      showInNav: false,
    },
  },

  PAGES: {
    path: '/workspaces/:workspaceId/pages/:pageId?',
    component: PagesRoot,
    metadata: {
      title: 'Pages',
      requiresAuth: true,
      showInNav: false,
    },
  },

  PAGES_ENTRY: {
    path: '/pages',
    component: PagesEntryRedirect,
    metadata: {
      title: 'Pages',
      requiresAuth: true,
      showInNav: false,
    },
  },

  BROWSER: {
    path: '/browser',
    component: lazy(() =>
      import('@platform/browser/frontend').then((m) => ({ default: m.BrowserModule }))
    ),
    metadata: {
      title: 'Browser',
      description: 'AI-controlled in-platform browser',
      requiresAuth: true,
      showInNav: false,
    },
  },

  PAGES_LEGACY_NOTE: {
    path: '/pages/legacy/:legacyNoteId',
    component: PagesLegacyNoteRedirect,
    metadata: {
      title: 'Pages',
      requiresAuth: true,
      showInNav: false,
    },
  },

  SETTINGS: {
    path: '/settings',
    component: SettingsPage,
    metadata: {
      title: 'Settings',
      requiresAuth: true,
      showInNav: false, // Navigation comes from nav.ts
    },
  },

  SETTINGS_PROFILE: {
    path: '/settings/profile',
    component: SettingsProfileRedirect,
    metadata: {
      title: 'Profile',
      requiresAuth: true,
      showInNav: false,
    },
  },

  SETTINGS_WORKSPACE: {
    path: '/settings/workspace',
    component: SettingsWorkspaceRedirect,
    metadata: {
      title: 'Workspace settings',
      requiresAuth: true,
      showInNav: false,
    },
  },

  /** Legacy deep link → Integrations hub tab [2.2.1604]. */
  SETTINGS_INTEGRATIONS: {
    path: '/settings/integrations',
    component: SettingsIntegrationsRedirect,
    metadata: {
      title: 'Integrations',
      requiresAuth: true,
      showInNav: false,
    },
  },

  /** Catch /settings/integrations/gmail (assistant connect_url) and nested legacy paths. */
  SETTINGS_INTEGRATIONS_SPLAT: {
    path: '/settings/integrations/*',
    component: SettingsIntegrationsRedirect,
    metadata: {
      title: 'Integrations',
      requiresAuth: true,
      showInNav: false,
    },
  },

  SETTINGS_SECURITY: {
    path: '/settings/security',
    component: SecuritySettingsPage,
    metadata: {
      title: 'Security',
      requiresAuth: true,
      showInNav: false,
    },
  },

  SESSIONS: {
    path: '/settings/sessions',
    component: SessionManagementPage,
    metadata: {
      title: 'Active Sessions',
      requiresAuth: true,
      showInNav: false,
    },
  },
  SETTINGS_SESSIONS: {
    path: '/settings/sessions',
    component: SessionManagementPage,
    metadata: {
      title: 'Active Sessions',
      requiresAuth: true,
      showInNav: false,
    },
  },

  SETTINGS_EMAIL_ACCOUNTS: {
    path: '/settings/email-accounts',
    component: lazy(() =>
      import('@domain/messaging').then((m) => ({ default: m.InboxSettingsPanel }))
    ),
    metadata: {
      title: 'Email accounts',
      requiresAuth: true,
      showInNav: false,
    },
  },

  SETTINGS_AI_USAGE: {
    path: '/settings/ai-usage',
    component: AIUsagePage,
    metadata: {
      title: 'AI Usage',
      requiresAuth: true,
      showInNav: false,
    },
  },

  SETTINGS_BILLING: {
    path: '/settings/billing',
    component: SettingsWorkspaceBillingPage,
    metadata: {
      title: 'Billing & plans',
      requiresAuth: true,
      showInNav: false,
    },
  },

  BILLING: {
    path: '/billing',
    component: BillingPage,
    metadata: {
      title: 'Billing',
      requiresAuth: true,
      showInNav: false, // Not in sidebar yet - accessible from Settings
    },
  },

  BILLING_UPGRADE: {
    path: '/billing/upgrade',
    component: BillingUpgradePage,
    metadata: {
      title: 'Upgrade Plan',
      requiresAuth: true,
      showInNav: false, // Not in sidebar - accessible from /billing
    },
  },

  /** Legacy bookmark — opens the conversation at /agent. */
  ASSISTANT: {
    path: '/home',
    component: AgentHomeRedirect,
    metadata: {
      title: 'Brazn',
      description: 'Opens the conversation at /agent',
      requiresAuth: true,
      showInNav: false,
    },
  },

  LANGFLOW_ASSISTANT: {
    path: '/langflow-assistant',
    component: AgentHomeRedirect,
    metadata: {
      title: 'Brazn',
      description: 'Opens the conversation at /agent',
      requiresAuth: true,
      showInNav: false,
    },
  },

  /** Bookmark/E2E alias — sidebar Assistant uses /langflow-assistant; /assistant must not 404 (#118). */
  ASSISTANT_ALIAS: {
    path: '/assistant',
    component: AgentHomeRedirect,
    metadata: {
      title: 'Brazn',
      description: 'Opens the conversation at /agent',
      requiresAuth: true,
      showInNav: false,
    },
  },

  // Account detail (unified CRM + SI account id in URL; legacy name slugs redirect via CompanyDetailEntry)
  COMPANY_DETAIL: {
    path: '/companies/:accountId',
    component: CompanyDetailEntry,
    metadata: {
      title: 'Company Details',
      requiresAuth: true,
      showInNav: false,
    },
  },

  // Alias so /company/:accountId works in addition to /companies/:accountId
  COMPANY_DETAIL_ALIAS: {
    path: '/company/:accountId',
    component: CompanyDetailEntry,
    metadata: {
      title: 'Company Details',
      requiresAuth: true,
      showInNav: false,
    },
  },

  TERRITORY: {
    path: '/territory',
    component: TerritoryPage,
    metadata: {
      title: 'Territory',
      requiresAuth: true,
      description: 'Territory management hub',
    },
  },

  DEALS: {
    path: '/deals',
    component: DealsPage,
    metadata: {
      title: 'Deals',
      requiresAuth: true,
      description: 'Deal pipeline management hub',
    },
  },

  /** Same hub as /deals with :opportunityId for detail tabs (Deal Assistant, timeline, etc.) */
  DEAL_DETAIL: {
    path: '/deals/:opportunityId',
    component: DealsPage,
    metadata: {
      title: 'Deal Details',
      requiresAuth: true,
      description: 'Single deal workspace',
      showInNav: false,
    },
  },

  SALES_COCKPIT: {
    path: '/sales-cockpit',
    component: SalesCockpitPage,
    metadata: {
      title: 'Sales Cockpit',
      requiresAuth: true,
      description: 'Ultimate Sales Cockpit - Pipeline overview and calendar',
      showInNav: false,
    },
  },

  PIPELINE_GENERATION: {
    path: '/pipeline-generation',
    component: PipelineGenerationPage,
    metadata: {
      title: 'Pipeline Generation',
      requiresAuth: true,
      description: 'AI-powered pipeline generation',
    },
  },

  SALES_HUB: {
    path: '/sales-hub',
    component: SalesEnablementHub,
    metadata: {
      title: 'Sales Hub',
      requiresAuth: true,
      description: 'Sales enablement hub',
    },
  },

  /** Nested CRM shell — layout `CrmModule`; child routes in `CrmModule.tsx` under `/crm/*`; `App.tsx` registers `path="/crm/*"` */
  CRM: {
    path: '/crm',
    component: CrmModule,
    metadata: {
      title: 'CRM',
      requiresAuth: true,
      description: 'CRM hub — territory, contacts, deals, tasks, pipeline generation',
      showInNav: false,
    },
  },

  // Contacts Management (C1 fix)
  CONTACTS: {
    path: '/contacts',
    component: ContactsPage,
    metadata: {
      title: 'Contacts',
      requiresAuth: true,
      description: 'Contact management hub',
    },
  },

  CONTACT_DETAIL: {
    path: '/contacts/:contactId',
    component: ContactsPage,
    metadata: {
      title: 'Contact Details',
      requiresAuth: true,
      showInNav: false,
    },
  },

  // Reports & Analytics (C2 fix)
  REPORTS: {
    path: '/reports',
    component: ReportsPage,
    metadata: {
      title: 'Reports',
      requiresAuth: true,
      description: 'Team performance and analytics',
    },
  },

  REPORTS_TEAM: {
    path: '/reports/team',
    component: ReportsPage,
    metadata: {
      title: 'Team Reports',
      requiresAuth: true,
      showInNav: false,
    },
  },

  REPORTS_PIPELINE: {
    path: '/reports/pipeline',
    component: ReportsPage,
    metadata: {
      title: 'Pipeline Reports',
      requiresAuth: true,
      showInNav: false,
    },
  },

  REPORTS_ACTIVITY: {
    path: '/reports/activity',
    component: ReportsPage,
    metadata: {
      title: 'Activity Reports',
      requiresAuth: true,
      showInNav: false,
    },
  },

  // Pipeline Analysis (C3 fix)
  PIPELINE_OPTIMIZE: {
    path: '/pipeline/optimize',
    component: PipelineOptimizePage,
    metadata: {
      title: 'Pipeline Optimization',
      requiresAuth: true,
      description: 'AI-powered pipeline recommendations',
    },
  },

  PIPELINE_COVERAGE: {
    path: '/pipeline/coverage',
    component: PipelineCoveragePage,
    metadata: {
      title: 'Pipeline Coverage',
      requiresAuth: true,
      description: 'Pipeline coverage analysis',
    },
  },

  PRESENTATIONS: {
    path: '/presentations',
    component: PresentationsPage,
    metadata: {
      title: 'Presentations',
      requiresAuth: true,
      description: 'Create and manage sales presentations',
    },
  },

  CALLS: {
    path: '/calls',
    component: CallsPage,
    metadata: {
      title: 'Calls',
      requiresAuth: true,
      description: 'Call recording and history',
    },
  },

  /** Public shared call clip (share token); no JWT — Prompt 10 */
  SHARED_CLIP: {
    path: '/clips/shared/:shareToken',
    component: SharedClipPlayerPage,
    metadata: {
      title: 'Shared Clip',
      requiresAuth: false,
      description: 'Public clip playback',
      showInNav: false,
    },
  },

  TEMPLATES: {
    path: '/templates',
    component: TemplateLibraryPage,
    metadata: {
      title: 'Template Library',
      requiresAuth: true,
      description: 'Presentation template management',
    },
  },

  TEMPLATE_MARKETPLACE: {
    path: '/templates/marketplace',
    component: TemplateMarketplacePage,
    metadata: {
      title: 'Template Marketplace',
      requiresAuth: true,
      description: 'Browse and share presentation templates',
    },
  },

  /** View/edit presentation - supports both /presentations/:id and /presentations/:id/edit */
  /** Shareable read-only viewer */
  PRESENTATION_VIEW_SHARE: {
    path: '/presentations/:id/view',
    component: PresentationEditorPage,
    metadata: {
      title: 'View Presentation',
      requiresAuth: false,
      description: 'Read-only presentation viewer',
    },
  },

  PRESENTATION_VIEW: {
    path: '/presentations/:id',
    component: PresentationEditorPage,
    metadata: {
      title: 'Presentation',
      requiresAuth: true,
      description: 'View or edit presentation',
    },
  },

  PRESENTATION_EDIT: {
    path: '/presentations/:id/edit',
    component: PresentationEditorPage,
    metadata: {
      title: 'Edit Presentation',
      requiresAuth: true,
      description: 'Slide editor',
    },
  },

  ACCOUNTS: {
    path: '/accounts',
    component: AccountsPage,
    metadata: {
      title: 'Accounts',
      requiresAuth: true,
      showInNav: false,
    },
  },

  // CRM account detail (hierarchy and contacts)
  ACCOUNT_DETAIL: {
    path: '/accounts/:accountId',
    component: lazy(() => import('../pages/AccountDetailPageRoute')),
    metadata: {
      title: 'Account Details',
      requiresAuth: true,
      showInNav: false,
    },
  },

  OPPORTUNITIES: {
    path: '/opportunities',
    component: LegacyOpportunitiesRedirect,
    metadata: {
      title: 'Sales Pipeline',
      requiresAuth: true,
      showInNav: false,
    },
  },

  FINANCIAL: {
    path: '/financial',
    component: FinancialPage,
    metadata: {
      title: 'Sales Performance',
      description: 'Quota attainment and pipeline analysis',
      requiresAuth: true,
      showInNav: false,
    },
  },

  FORECAST: {
    path: '/forecast',
    component: ForecastPage,
    metadata: {
      title: 'Forecast',
      description: 'Pipeline and forecast analytics',
      requiresAuth: true,
      showInNav: false,
    },
  },

  PRODUCTS: {
    path: '/products',
    component: ProductsPage,
    metadata: {
      title: 'My Products',
      requiresAuth: true,
      showInNav: false,
    },
  },

  SALES_GUIDES: {
    path: '/sales-guides',
    component: SalesGuidePage,
    metadata: {
      title: 'My Sales Guides',
      requiresAuth: true,
      showInNav: false,
    },
  },

  /** App root + AppLayout only — keep out of `/sales-hub/*` nested routes (Bug 14 layout bleed). */
  ACTIVITIES: {
    path: '/activities',
    component: ActivityCenterPage,
    metadata: {
      title: 'Activity Center',
      requiresAuth: true,
      showInNav: false,
    },
  },

  /** RevOps world-model timeline (Sprint 10) — distinct from notification Activity Center at `/activities`. */
  REVOPS_ACTIVITY_FEED: {
    path: '/activity',
    component: RevOpsActivityFeedPage,
    metadata: {
      title: 'Activity Feed',
      description: 'RevOps activity timeline across deals, contacts, and integrations',
      requiresAuth: true,
      showInNav: false,
    },
  },

  CALENDAR_EVENT: {
    path: '/calendar/events/:eventId',
    component: EventEditorPage,
    metadata: {
      title: 'Event',
      description: 'Full-screen calendar event editor',
      requiresAuth: true,
      showInNav: false,
    },
  },

  CALENDAR: {
    path: '/calendar',
    component: CalendarPage,
    metadata: {
      title: 'Calendar',
      description: 'Event management, deal milestones, and daily agenda',
      requiresAuth: true,
      showInNav: false,
    },
  },

  MESSAGING: {
    path: '/messaging',
    component: lazy(() =>
      import('@domain/messaging').then((m) => ({ default: m.MessagingPage }))
    ),
    metadata: {
      requiresAuth: true,
      title: 'Messaging',
      description: 'Email management and campaigns',
      showInNav: false,
    },
  },

  EMAIL: {
    path: '/email',
    component: EmailToMessagingRedirect,
    metadata: {
      requiresAuth: true,
      title: 'Email',
      description: 'Redirect to Messaging (canonical inbox)',
      showInNav: false,
    },
  },

  MESSAGING_OAUTH_CALLBACK: {
    path: '/messaging/oauth/callback',
    component: lazy(() =>
      import('@domain/messaging').then((m) => ({ default: m.OAuthCallbackPage }))
    ),
    metadata: {
      requiresAuth: true,
      title: 'Connect Inbox',
      description: 'OAuth redirect for inbox connection',
      showInNav: false,
    },
  },

  MESSAGING_INTEGRATIONS_OAUTH_CALLBACK: {
    path: '/messaging/integrations/oauth/callback',
    component: lazy(() =>
      import('@domain/messaging').then((m) => ({ default: m.IntegrationsOAuthCallbackPage }))
    ),
    metadata: {
      requiresAuth: true,
      title: 'Connect integration',
      description: 'OAuth redirect for Slack / Microsoft Teams messaging integrations',
      showInNav: false,
    },
  },

  REVOPS: {
    path: '/revops',
    component: RevOpsDashboardPage,
    metadata: {
      title: 'RevOps Intelligence',
      description: 'Engagement scoring, health tracking, and risk assessment',
      requiresAuth: true,
      showInNav: false,
    },
  },

  OPPORTUNITY_DETAIL: {
    path: '/opportunities/:opportunityId',
    component: LegacyOpportunitiesRedirect,
    metadata: {
      title: 'Opportunity Details',
      requiresAuth: true,
      showInNav: false,
    },
  },

  // Sales Profile - Company setup, product catalog, AI guide generation
  SALES_PROFILE: {
    path: '/sales-profile',
    component: SalesProfilePage,
    metadata: {
      title: 'Sales Profile',
      description: 'Company profile, product catalog, and AI sales guides',
      requiresAuth: true,
      showInNav: false,
    },
  },

  // Alias for backward compatibility
  SALES_PROFILE_ALIAS: {
    path: '/sales-assistant',
    component: SalesProfilePage,
    metadata: {
      title: 'Sales Assistant',
      requiresAuth: true,
      showInNav: false,
    },
  },

  // LAB ROUTE: Do not add to main navigation yet.
  // Exposed via direct URL / deep link only until production-ready.
  TEST_RESEARCH: {
    path: '/test-research',
    component: TestResearchPage,
    metadata: {
      title: 'Sales Intelligence Test',
      requiresAuth: true,
      showInNav: false, // Lab route - not in nav
    },
  },

  // LAB ROUTE: Dev/E2E fixture — CRM linked pages panels without full CRM drawer shell [2.2.1199].
  CRM_BRAIN_FIXTURE: {
    path: '/dev/crm-brain-fixture',
    component: CrmBrainFixturePage,
    metadata: {
      title: 'CRM Brain Fixture',
      requiresAuth: true,
      showInNav: false,
    },
  },

  // LAB ROUTE: Do not add to main navigation yet.
  // Exposed via direct URL / deep link only until production-ready.
  WORKFLOW_SETTINGS: {
    path: '/settings/workflows',
    component: WorkflowSettingsPage,
    metadata: {
      title: 'Workflow Settings',
      requiresAuth: true,
      showInNav: false, // Lab route - not in nav
    },
  },

  // LAB ROUTE: Do not add to main navigation yet.
  // Exposed via direct URL / deep link only until production-ready.
  WORKFLOWS: {
    path: '/workflows',
    component: WorkflowsPage,
    metadata: {
      title: 'Workflows',
      requiresAuth: true,
      showInNav: false, // Lab route - not in nav
    },
  },

  // LAB ROUTE: Do not add to main navigation yet.
  // Exposed via direct URL / deep link only until production-ready.
  WORKFLOW_NEW: {
    path: '/workflows/new',
    component: WorkflowEditorPage,
    metadata: {
      title: 'New Workflow',
      requiresAuth: true,
      showInNav: false, // Lab route - not in nav
    },
  },

  // LAB ROUTE: Do not add to main navigation yet.
  // Exposed via direct URL / deep link only until production-ready.
  WORKFLOW_DETAIL: {
    path: '/workflows/:workflowId',
    component: WorkflowEditorPage,
    metadata: {
      title: 'Workflow Editor',
      requiresAuth: true,
      showInNav: false, // Lab route - not in nav
    },
  },

  // LAB ROUTE: Do not add to main navigation yet.
  // Exposed via direct URL / deep link only until production-ready.
  RESEARCH: {
    path: '/research',
    component: ResearchPage,
    metadata: {
      title: 'Research',
      requiresAuth: true,
      showInNav: false, // Lab route - not in nav
    },
  },

  // Super Admin routes
  SUPER_ADMIN_DASHBOARD: {
    path: '/super-admin',
    component: SuperAdminDashboardPage,
    metadata: {
      title: 'Super Admin Dashboard',
      requiresAuth: true,
      requiresRoles: ['super_admin'],
      showInNav: false,
    },
  },

  SUPER_ADMIN_TENANTS: {
    path: '/super-admin/tenants',
    component: TenantManagementPage,
    metadata: {
      title: 'Tenant Management',
      requiresAuth: true,
      requiresRoles: ['super_admin'],
      showInNav: false,
    },
  },

  SUPER_ADMIN_USERS: {
    path: '/super-admin/users',
    component: UserManagementPage,
    metadata: {
      title: 'User Management',
      requiresAuth: true,
      requiresRoles: ['super_admin'],
      showInNav: false,
    },
  },

  SUPER_ADMIN_SETTINGS: {
    path: '/super-admin/settings',
    component: SystemSettingsPage,
    metadata: {
      title: 'System Settings',
      requiresAuth: true,
      requiresRoles: ['super_admin'],
      showInNav: false,
    },
  },

  SUPER_ADMIN_FEATURES: {
    path: '/super-admin/features',
    component: FeatureFlagsPage,
    metadata: {
      title: 'Feature Flags',
      requiresAuth: true,
      requiresRoles: ['super_admin'],
      showInNav: false,
    },
  },

  // Fallback
  NOT_FOUND: {
    path: '*',
    component: NotFoundPage,
    metadata: {
      title: 'Not Found',
      requiresAuth: false,
      showInNav: false,
    },
  },
} as const;

/**
 * Get navigation items sorted by navOrder
 * 
 * @deprecated Navigation items now come from nav.ts
 * This function is kept for backward compatibility but should not be used.
 * Use mainNavItems and adminNavItems from '../config/nav' instead.
 */
export function getNavItems(): RouteConfig[] {
  return Object.values(ROUTES)
    .filter((route) => route.metadata.showInNav)
    .sort((a, b) => (a.metadata.navOrder || 999) - (b.metadata.navOrder || 999));
}

/**
 * Get route by path for reverse lookups
 */
export function getRouteByPath(path: string): RouteConfig | undefined {
  return Object.values(ROUTES).find((route) => route.path === path);
}
